import { ref, set, getCurrentInstance } from '@vue/composition-api'
import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import useCommon from '@/views/organization/useCommon'
import { oid } from '@/helpers/mongoHelpers'
import store from '@/store'
import { permissionScope, hasPermission } from '@/composables/usePermission'

export default function useConfirmationView() {
  const { showErrorMessage } = useNotifications()
  const { route, router } = useRouter()
  const { getItems, getItemsAggregate } = useLambda()
  const { downloadZip } = useCommon()

  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== 'admin' ? userData.client.$oid : null
  const userId = userData.worker_id != null ? userData.worker_id.$oid : null
  const userObjectId = oid(userId)

  const confirmation = ref({})
  const printableConfirmation = ref({})
  const isLoading = ref(true)
  const newRRCalculation = ref(null)

  const show = async (isPrintable = false) => {
    try {
      const query = { _id: oid(route.value.params.id) }
      if (clientId) query.client_id = oid(clientId)

      const restrictToOwn = permissionScope('confirmations-daily', 'view') === 'own'
      const scopeMatch = restrictToOwn
        ? { $or: [
            { 'worker._id': userId },
            { 'process.responsibles': userObjectId },
            { 'process.leadResponsibles': userObjectId },
            { 'process.evaluator': userObjectId },
          ] }
        : {}

      const pipeline = [
        { $match: query },
        { $lookup: { from: 'worker', localField: 'worker', foreignField: '_id', pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ], as: 'worker' } },
        { $lookup: { from: 'worker', localField: 'supervisor', foreignField: '_id', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ], as: 'supervisor' } },
        { $lookup: { from: 'process', localField: 'process', foreignField: '_id', pipeline: [ { $project: { name: 1, code: 1, goal: 1, description: 1, requireImage: 1, residualRisk: 1, responsibles: 1, leadResponsibles: 1, evaluator: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ], as: 'process' } },
        { $addFields: { _id: { $toString: "$_id" }, worker: { $arrayElemAt: ["$worker", 0] }, supervisor: { $arrayElemAt: ["$supervisor", 0] }, process: { $arrayElemAt: ["$process", 0] } } },
        { $match: scopeMatch },
        { $unset: ['process.responsibles', 'process.leadResponsibles', 'process.evaluator'] },
      ]

      const items = await getItemsAggregate({ collection: 'confirmation', pipeline })
      if (!items?.[0]) throw new Error('Item not found')

      if (isPrintable) {
        printableConfirmation.value = items[0]
        printableConfirmation.value.activities?.forEach(e => set(e, 'showBehaviours', true))
      } else {
        confirmation.value = items[0]
        confirmation.value.activities?.forEach(e => set(e, 'showBehaviours', false))

        // Obtener el cálculo del nuevo RR desde la confirmación (si existe)
        if (confirmation.value.previousRR && confirmation.value.newRR) {
          newRRCalculation.value = {
            previousRR: confirmation.value.previousRR,
            newRR: confirmation.value.newRR,
            level: confirmation.value.evaluationLevel,
            compliancePercentage: confirmation.value.compliancePercentage
          }
        }
      }

      // Fetch cases generated in this evaluation
      try {
        const improvementQuery = { confirmation: oid(route.value.params.id) }
  
        const items = await getItems({ collection: 'improvement', query: improvementQuery })
  
        if (items?.length) {
          for (const caseGenerated of items) {
            if (!caseGenerated.activity || !caseGenerated.behaviour) continue

            for (const activity of confirmation.value.activities) {
              if (caseGenerated.activity !== activity.name) continue

              for (const behaviour of activity.behaviours) {
                if (caseGenerated.behaviour.name !== behaviour.name) continue

                set(behaviour, 'caseId', caseGenerated._id.toString())

                break
              }

              break
            }
          }
        }
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.err_improvement_list'))
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.confirmation_fetch_error'))
    } finally {
      isLoading.value = false
    }
  }

  const activityScore = (activity) => {
    if (!activity.total) return "N/A"
    const ratio = (activity.score / activity.total) * 100
    return `${ratio.toFixed(1)} %`
  }

  const reEvaluate = (confirmationId) => {
    if (!hasPermission('confirmations-daily', 'edit')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    router.push({ name: 'habit-confirmation-re-evaluate', params: { id: confirmationId } })
  }

  // Obtener comportamientos negativos
  const getNegativeBehaviours = () => {
    const negative = []
    confirmation.value.activities?.forEach(activity => {
      activity.behaviours?.forEach(behaviour => {
        // Un comportamiento es negativo si la respuesta no coincide con la respuesta esperada
        if (behaviour.answer === "true" && !behaviour.expectedAnswer || behaviour.answer === "false" && behaviour.expectedAnswer) {
          negative.push({
            activityName: activity.name,
            behaviourName: behaviour.name,
            behaviourAnswer: behaviour.answer,
            behaviourComment: behaviour.comment || '',
          })
        }
      })
    })
    return negative
  }

  const createMultiBehaviourCase = () => {
    const negativeBehaviours = getNegativeBehaviours()
    const routeData = router.resolve({
      name: 'organization-case-add',
      query: {
        fromEvaluation: 'true',
        evaluationId: confirmation.value._id,
        behaviours: JSON.stringify(negativeBehaviours)
      }
    })
    window.open(routeData.href, '_blank')
  }

  // Download to PDF
  const { proxy } = getCurrentInstance()
  const isDownloading = ref(false)

  const downloadPDF = async () => {
    if (!hasPermission('confirmations-daily', 'export')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    try {
      isDownloading.value = true
      await show(true)
      const data = printableConfirmation.value

      // Eliminar cualquier contenedor de impresión anterior
      const oldContainer = document.getElementById('print-container')
      if (oldContainer) oldContainer.remove()

      // Crear contenedor oculto
      const container = document.createElement('div')
      container.id = 'print-container'
      container.style.position = 'fixed'
      container.style.top = '-9999px'
      container.style.left = '-9999px'
      document.body.appendChild(container)

      // Crear y montar el componente imprimible
      const FormConstructor = proxy.$root.$options.components.EvaluationPrintableView
      const vm = new proxy.$root.constructor({
        parent: proxy.$root,
        render: (h) => h(FormConstructor, { props: { confirmation: data } }),
      }).$mount()

      container.appendChild(vm.$el)

      // Esperar un frame para asegurar render completo
      await new Promise(resolve => setTimeout(resolve, 150))

      // Cambiar posición para impresión
      container.style.top = '0'
      container.style.left = '0'

      // Función de limpieza
      const cleanup = () => {
        vm.$destroy()
        if (container.parentNode) container.parentNode.removeChild(container)
        window.removeEventListener('afterprint', cleanup)
        isDownloading.value = false
      }

      // Escuchar cuando termina la impresión (incluso si se cancela)
      window.addEventListener('afterprint', cleanup, { once: true })

      // Abrir cuadro de impresión
      window.print()

      // Descargar todas los documentos y evidencias de la denuncia
      if (data.allFileKeys?.length) {
        await downloadZip(data.allFileKeys)
      }
    } catch (error) {
      console.error('Error al imprimir:', error)
      isDownloading.value = false
    }
  }

  return {
    show,
    confirmation,
    activityScore,
    isLoading,
    reEvaluate,
    downloadPDF,
    isDownloading,
    createMultiBehaviourCase,
    newRRCalculation,
  }
}
